/**
 * AdvancedUpgradeCard — manual M-Pesa Paybill upgrade to KES 1,000/30 days
 * Advanced Trader tier.
 *
 * Feb 12 2026 · replaces the earlier third-party card-processor flow. The card opens
 * `PaybillModal` where the user submits an M-Pesa confirmation code;
 * activation happens at T+1 midnight UTC after Super Admin verification.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Sparkles, Clock, ShieldCheck, CreditCard } from "lucide-react";
import PaybillModal from "@/components/subscription/PaybillModal";
import StripeCheckoutModal from "@/components/subscription/StripeCheckoutModal";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { isTrueInstitutional } from "@/lib/tier";

const DISPLAY_CURRENCIES = ["KES", "USD", "EUR", "GBP"];

export default function AdvancedUpgradeCard() {
    const { user, refresh } = useAuth();
    const { currency, setCurrency, formatMoney } = useCurrency();
    const [open, setOpen] = useState(false);
    const [stripeOpen, setStripeOpen] = useState(false);
    const [pending, setPending] = useState(null);
    const [trial, setTrial] = useState(null);

    async function loadMine() {
        try {
            const { data } = await api.get("/paybill/mine?limit=5");
            const first = (data.submissions || []).find(
                (s) => s.status === "pending_super_admin_verification",
            );
            setPending(first || null);
        } catch { /* silent */ }
    }
    useEffect(() => { loadMine(); }, []);
    useEffect(() => {
        api.get("/trial/status").then(({ data }) => setTrial(data)).catch(() => setTrial(null));
    }, []);

    // Institutional seats (broker-bundled) never see this upsell — their
    // broker handles billing. Advanced-tier users DO see it even though
    // they already have full access, because — for now — every Advanced
    // account is on the 7-day free trial and still needs a way to convert
    // to a paid subscription before day 7 (2026-09-01 directive).
    if (isTrueInstitutional(user)) {
        return null;
    }
    const isTrialActive = trial?.active && user?.tier === "advanced";

    return (
        <div className="rounded-2xl border-2 border-primary bg-primary/5 p-5" data-testid="advanced-upgrade-card">
            <div className="flex items-start gap-3">
                <Sparkles className="w-6 h-6 text-primary shrink-0" />
                <div className="flex-1 min-w-0">
                    {isTrialActive && (
                        <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-primary/15 text-primary text-xs font-semibold px-2.5 py-1" data-testid="advanced-upgrade-trial-chip">
                            <Clock className="w-3 h-3" />
                            {trial.status === "GRACE"
                                ? "Your free trial has ended — grace period active"
                                : `${trial.days_remaining} day${trial.days_remaining === 1 ? "" : "s"} left in your free trial`}
                        </div>
                    )}
                    <div className="overline text-primary tracking-widest">
                        {isTrialActive ? "Keep Obondo Advanced" : "Advanced Trader"}
                    </div>
                    <div className="text-2xl font-black tracking-tight" data-testid="advanced-upgrade-price">
                        {formatMoney(1000, "KES")} <span className="text-muted-foreground text-sm font-normal">/ 30 days</span>
                    </div>
                    <div className="flex items-center gap-1 mt-1.5" data-testid="advanced-upgrade-currency-toggle">
                        {DISPLAY_CURRENCIES.map((c) => (
                            <button
                                key={c}
                                type="button"
                                onClick={() => setCurrency(c)}
                                data-testid={`advanced-upgrade-ccy-${c.toLowerCase()}`}
                                aria-pressed={currency === c}
                                className={
                                    "text-xs px-2 py-0.5 rounded-full border font-semibold transition-colors " +
                                    (currency === c
                                        ? "bg-primary text-primary-foreground border-primary"
                                        : "border-border text-muted-foreground hover:border-primary/50 hover:text-foreground")
                                }
                            >
                                {c}
                            </button>
                        ))}
                    </div>
                    <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                        {isTrialActive ? (
                            <>
                                <li>• Subscribe now so nothing interrupts your access after day 7</li>
                                <li>• Personalised Obondo AI trading recommendations</li>
                                <li>• Full analytics — Sharpe, drawdown, Greeks</li>
                            </>
                        ) : (
                            <>
                                <li>• Unlock options, shorts, and derivatives</li>
                                <li>• Personalised Obondo AI trading recommendations</li>
                                <li>• Full analytics — Sharpe, drawdown, Greeks</li>
                            </>
                        )}
                    </ul>
                    {pending ? (
                        <div className="mt-4 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-xs space-y-1"
                             data-testid="advanced-upgrade-pending">
                            <div className="flex items-center gap-1.5 font-semibold text-amber-500">
                                <Clock className="w-3.5 h-3.5" /> Awaiting Super Admin verification
                            </div>
                            <div>Reference <span className="mono">{pending.mpesa_ref}</span> · submitted {new Date(pending.submitted_at).toLocaleDateString()}</div>
                            <div className="text-muted-foreground">Your subscription starts at the next midnight (UTC) once verified.</div>
                        </div>
                    ) : (
                        <div className="mt-4 flex flex-col sm:flex-row gap-2">
                            <Button
                                className="flex-1"
                                onClick={() => setOpen(true)}
                                data-testid="advanced-upgrade-btn"
                            >
                                {isTrialActive ? "Subscribe via M-Pesa Paybill" : "Pay via M-Pesa Paybill"}
                            </Button>
                            <Button
                                variant="outline"
                                className="flex-1"
                                onClick={() => setStripeOpen(true)}
                                data-testid="advanced-upgrade-stripe-btn"
                            >
                                <CreditCard className="w-4 h-4 mr-1.5" /> Pay in USD or EUR
                            </Button>
                        </div>
                    )}
                    <div className="text-[10px] text-muted-foreground mt-2 flex items-center gap-1">
                        <ShieldCheck className="w-3 h-3" /> Two rails · manual Paybill for KES · Stripe for global card / SEPA
                    </div>
                </div>
            </div>
            <PaybillModal
                open={open}
                planCode="advanced_monthly"
                onClose={() => setOpen(false)}
                onSubmitted={async () => {
                    await loadMine();
                    await refresh?.();
                    toast.success("Submitted for verification");
                }}
            />
            <StripeCheckoutModal
                open={stripeOpen}
                packageId="advanced_monthly"
                tierLabel="Advanced Trader — monthly"
                onClose={() => setStripeOpen(false)}
            />
        </div>
    );
}
